import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ImmutablePropTypes from "react-immutable-proptypes";
import classNames from "classnames";
import { Client, Language } from "resourceModels";
import { getResource, getResources } from "selectors/resources";
import Button from "components/Common/Button";
import dialogWrapper from "components/Common/Dialog";
import ResponseEditorLanguagesDialog from "components/Pages/Responses/ResponsesEditor/ResponseEditorLanguages/ResponseEditorLanguagesDialog";

import "./style.scss";

const EnhancedResponseEditorLanguagesDialog = dialogWrapper(
  ResponseEditorLanguagesDialog
);

const BLOCK_NAME = "LanguageButton";

const DEFAULT_POSITION = {
  left: 0,
  top: 0
};

export class LanguageButton extends React.Component {
  /**
   * Constructor
   *
   * @param {Object} props
   */
  constructor(props) {
    super(props);

    this.languageButtonRef = React.createRef();

    this.state = {
      isLanguageDialogOpen: false,
      position: DEFAULT_POSITION
    };

    this.handleToggleLanguagesDialog = this.handleToggleLanguagesDialog.bind(this);
    this.adjustButtonPositionOnScroll = this.adjustButtonPositionOnScroll.bind(this);
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    const position = this.getOffset();

    this.setState({
      position
    });

    document.addEventListener("scroll", this.adjustButtonPositionOnScroll, true);
  }

  /**
   * componentDidUpdate
   * @param {Object} prevProps
   * @param {Object} prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const { isLanguageDialogOpen } = this.state;

    if (prevState.isLanguageDialogOpen !== isLanguageDialogOpen) {
      const position = this.getOffset();

      this.setState({
        position
      });
    }
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    document.removeEventListener("scroll", this.adjustButtonPositionOnScroll, true);
  }

  /**
   * getOffset
   * @returns {Object}
   */
  getOffset() {
    const rect = this.languageButtonRef.current.getBoundingClientRect();
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

    return {
      left: rect.left + scrollLeft,
      top: rect.top + scrollTop
    };
  }

  /**
   * Recalculates the dropdown's position when content is scrolled
   *
   * @param {ScrollEvent} event
   */
  adjustButtonPositionOnScroll(event) {
    if (
      this.languageButtonRef.current &&
      !this.languageButtonRef.current.contains(event.target)
    ) {
      const position = this.getOffset();
      this.setState({
        position
      });
    }
  }

  /**
   * handleToggleLanguagesDialog
   */
  handleToggleLanguagesDialog() {
    this.setState(prevState => ({
      isLanguageDialogOpen: !prevState.isLanguageDialogOpen
    }));
  }

  /**
   * renderEnhancedResponseEditorLanguagesDialog
   *
   * @returns {React}
   */
  renderDialog() {
    const {
      availableLanguages,
      addLanguage,
      inModal,
      absolute
    } = this.props;
    const {
      isLanguageDialogOpen,
      position
    } = this.state;

    return (
      <EnhancedResponseEditorLanguagesDialog
        closeDialog={() => this.setState({ isLanguageDialogOpen: false })}
        availableLanguages={availableLanguages}
        onLanguageClick={addLanguage}
        isOpen={isLanguageDialogOpen}
        title="Add Translation"
        alignment="right"
        width={200}
        position={inModal ? position : null}
        inModal={inModal}
        absolute={absolute}
      />
    );
  }

  /**
   * renderEnhancedResponseEditorLanguagesDialog
   *
   * @returns {React}
   */
  renderUsingPortal() {
    const { inModal } = this.props;

    const targetElement = inModal ? document.getElementById("modal") : document.body;

    return (
      <div>
        {ReactDOM.createPortal(
          this.renderDialog(),
          targetElement
        )}
      </div>
    );
  }


  /**
   * render
   *
   * @returns {React}
   */
  render() {
    const {
      /** @type Client */
      client,
      availableLanguages,
      languages,
      color,
      absolute
    } = this.props;

    return (
      <div
        className={classNames(
          "LanguageButton", {
            "LanguageButton--hidden": (
              availableLanguages.size === 0 && (
                languages.size - 1 === client.translatedLanguages.size
              )
            )
          }
        )}
        ref={this.languageButtonRef}
      >
        <Button
          text="Add"
          title="Add Language"
          size="small"
          icon="Globe"
          customClassName={classNames(
            `${BLOCK_NAME}__add`, {
              [`${BLOCK_NAME}__add--color-white`]: color === "white"
            }
          )}
          onClick={this.handleToggleLanguagesDialog}
        />
        {absolute ? this.renderUsingPortal() : this.renderDialog()}
      </div>
    );
  }
}

LanguageButton.propTypes = {
  availableLanguages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  languages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  color: PropTypes.oneOf(["white", null]),
  client: PropTypes.instanceOf(Client).isRequired,
  absolute: PropTypes.bool,
  inModal: PropTypes.bool,
  addLanguage: PropTypes.func.isRequired
};

LanguageButton.defaultProps = {
  color: null,
  absolute: false,
  inModal: false
};

/**
 * @param {Object} state
 * @returns {Object}
 */
function mapStateToProps(state) {
  return {
    client: getResource(state, "client"),
    languages: getResources(state, "language")
  };
}

const Connector = connect(mapStateToProps)(LanguageButton);

Connector.propTypes = {
  availableLanguages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  addLanguage: PropTypes.func.isRequired
};

export default Connector;
